import { useEffect, useState } from 'react';
import { loginFields } from '../constants/formFields';
import Input from './Input';
import FormAction from './FormAction';
import FormSocial from './FormSocial';
import { Errors } from '../constants/errors';
import { getRedirectUrlFromParam } from '../lib/helpers';
import { Link } from 'react-router-dom';

interface FormElements extends HTMLFormControlsCollection {
  emailAddress: HTMLInputElement;
  password: HTMLInputElement;
}
interface LoginFormElement extends HTMLFormElement {
  readonly elements: FormElements;
}

const fields = loginFields;
const fieldsState: { [key: string]: string } = {};
fields.forEach((field) => (fieldsState[field.id] = ''));

export default function Login() {
  const [loginState, setLoginState] = useState(fieldsState);
  const [formErrorState, setFormErrorState] = useState('');

  useEffect(() => {
    const url = new URL(window.location.href);

    if (url.searchParams.get('email')) {
      // we got an email, update the field
      setLoginState({
        ...loginState,
        emailAddress: url.searchParams.get('email') || '',
      });
    }
  }, [loginState]);

  const handleChange = (e: React.FormEvent<HTMLInputElement>) => {
    const btn = e.target as HTMLInputElement;
    setLoginState({ ...loginState, [btn.id]: btn.value });
  };

  const handleSubmit = async (e: React.FormEvent<LoginFormElement>) => {
    e.preventDefault();
    setFormErrorState('');

    const alternateRedirectUrl = getRedirectUrlFromParam();

    const response = await fetch(`/auth/login${window.location.search}`, {
      method: 'POST',
      headers: {
        'content-type': 'application/x-www-form-urlencoded;charset=UTF-8',
        accept: 'application/json',
      },
      body: new URLSearchParams({
        email: e.currentTarget.elements.emailAddress.value,
        password: e.currentTarget.elements.password.value,
        redirectUrl: alternateRedirectUrl || '',
      }),
    });
    const payload = await response.json();
    if (payload.error && payload.code === 123) {
      setFormErrorState(Errors.InvalidEmailPasswordCombo);
      return;
    }

    if (payload.redirectUrl) {
      window.location.href = payload.redirectUrl;
    } else {
      // navigate('/account');
      // TODO this is a hack for now, we need to fix
      window.location.href = 'https://app.bloodygoodtests.com.au';
    }
  };

  const getSignupUrl = (): string => {
    return `/signup${window.location.search}`;
  };

  // if (showRedesign) {
  //   return <div>Redesign</div>;
  // }

  return (
    <form className="w-full" onSubmit={handleSubmit}>
      {formErrorState && (
        <div
          className="bg-eirly-100/10 border-t-4 border-eirly-500 rounded-b text-teal-900 px-4 py-3 shadow-md"
          role="alert"
        >
          <div className="flex">
            <div className="py-1">
              <svg
                className="fill-current h-6 w-6 text-eirly-500 mr-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
              >
                <path d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm12.73-1.41A8 8 0 1 0 4.34 4.34a8 8 0 0 0 11.32 11.32zM9 11V9h2v6H9v-4zm0-6h2v2H9V5z" />
              </svg>
            </div>
            <div>
              <p className="font-bold">Error</p>

              {formErrorState === Errors.UnknownError && (
                <p className="text-sm">
                  Unable to create account, perhaps try{' '}
                  <Link
                    className="text-eirly-600 hover:text-eirly-500"
                    to={getSignupUrl()}
                  >
                    signing up
                  </Link>{' '}
                  instead?
                </p>
              )}
              {formErrorState === Errors.ServerError && (
                <p className="text-sm">
                  Server returned an error, please try again later
                </p>
              )}
              {formErrorState === Errors.InvalidEmailPasswordCombo && (
                <p className="text-sm">
                  No matching account for the email/password combination
                  provided.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="-space-y-px">
        {fields.map((field) => (
          <Input
            key={field.id}
            handleChange={handleChange}
            value={loginState[field.id]}
            labelText={field.labelText}
            labelFor={field.labelFor}
            id={field.id}
            name={field.name}
            type={field.type}
            isRequired={field.isRequired}
            placeholder={field.placeholder}
            extra={field.extra}
            tabIndex={field.tabIndex}
          />
        ))}
      </div>
      <FormAction text="Login" disabled={false} tabIndex={3} />
      <FormSocial />
    </form>
  );
}
